import React, { useCallback, useEffect, useRef, useState } from "react";

import styles from "./AuthModal.module.css";
import Api from "../../utils/Api/Api";
import { Sheet } from "../Sheet/Sheet";
import { Turnstile } from "../Turnstile/Turnstile";
import { GoogleSignIn, GOOGLE_CLIENT_ID } from "../GoogleSignIn/GoogleSignIn";
import { AppleSignIn, APPLE_SERVICES_ID } from "../AppleSignIn/AppleSignIn";
import { isIndia, getMarket, marketInfo } from "../../utils/market";
import { track, identify, EVENTS } from "../../utils/analyticsService";

const OTP_LENGTH = 4;
const RESEND_SECONDS = 30;
const NAME_MAX = 30;
// Same rule as the backend's register: letters and spaces only.
const NAME_PATTERN = /^[a-zA-Z\s]+$/;

// The app's axios interceptor (App.js) resolves failed requests with the
// error object instead of rejecting. Turn those back into throws so a refused
// OTP or registration can't be mistaken for success.
const ok = (res) => {
  if (!res || res.isAxiosError || res instanceof Error || !("data" in res)) throw res;
  return res;
};

const failReason = (err) =>
  err?.response?.status ? `http_${err.response.status}` : "network_error";

/**
 * Login as a bottom sheet, from design/Purchase Flow.dc.html (loginOpen
 * block). The page underneath never unmounts, so plan selection, coupon and
 * scroll position survive signing in.
 *
 * Props: open, onClose, onSuccess, reason, allMethods, context. The subtitle is the
 * design's fixed line; outside India, where the sheet is Google (and Apple
 * once configured), `reason` (what the caller is signing in for) replaces the
 * store-buying line when given. `context` (buy, gift…) only labels the
 * sign_in_* analytics events.
 *
 * The store decides the method, because it decides what is sold and how it
 * is paid for. `allMethods` drops that rule for a caller where the store is
 * beside the point - connecting an AI assistant to an existing account - and
 * offers every way the apps sign in: an Indian number, Google and Apple, the
 * store's own way first. Without it, somebody who signed up in the iPhone app
 * with Apple, or an Indian customer abroad, could not reach their account.
 *
 * Uses the v2 auth endpoints: /users/v2/login sends an OTP to any number,
 * /users/v2/verify_otp either signs an existing customer in or answers
 * is_new_user, and then /users/v2/register creates the account from a name.
 * The v1 /users/login refuses numbers with no account (400), which left new
 * customers stuck on the phone step with no way to sign up.
 */
export const AuthModal = ({ open, onClose, onSuccess, reason, allMethods = false, context: purpose = "other" }) => {
  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  // Google or Apple signing in; kept apart so the phone button never says "Sending…".
  const [providerBusy, setProviderBusy] = useState(false);
  const [error, setError] = useState("");
  const [isNewUser, setIsNewUser] = useState(false);
  const [name, setName] = useState("");
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileReset, setTurnstileReset] = useState(0);

  const phoneRef = useRef(null);
  const otpRef = useRef(null);
  const nameRef = useRef(null);
  // Set once this opening of the sheet signs someone in, so closing after that
  // isn't counted as giving up.
  const signedInRef = useRef(false);
  const phoneValid = /^[6-9]\d{9}$/.test(phone);

  useEffect(() => {
    if (!open) return;
    setStep("phone");
    setOtp("");
    setError("");
    setBusy(false);
    setIsNewUser(false);
    setName("");
    signedInRef.current = false;
    const providerMethods = [GOOGLE_CLIENT_ID && "google", APPLE_SERVICES_ID && "apple"].filter(Boolean);
    const methods = allMethods ? ["phone", ...providerMethods] : isIndia() ? ["phone"] : providerMethods;
    track(EVENTS.SIGN_IN_SHOWN, { purpose, market: getMarket(), methods: methods.join(",") || "none" });
    // Where Google and Apple lead, don't raise a phone keyboard over them.
    if (allMethods && !isIndia()) return undefined;
    const t = setTimeout(() => phoneRef.current?.focus(), 120);
    return () => clearTimeout(t);
  }, [open, allMethods]); // eslint-disable-line react-hooks/exhaustive-deps -- once per opening

  useEffect(() => {
    const target = step === "otp" ? otpRef : step === "name" ? nameRef : null;
    if (!target) return;
    const t = setTimeout(() => target.current?.focus(), 120);
    return () => clearTimeout(t);
  }, [step]);

  // Sends the OTP. The Turnstile token only matters for numbers with no
  // account yet; it is single-use, so ask for a fresh one after every send.
  const requestOtp = useCallback(
    async (isResend) => {
      const payload = { phone };
      if (turnstileToken) payload.turnstile_token = turnstileToken;
      try {
        ok(await Api.GetOtpV2(payload));
        track(EVENTS.OTP_REQUESTED, { is_resend: isResend, context: "auth_modal" });
      } catch (err) {
        track(EVENTS.OTP_REQUEST_FAILED, { reason: failReason(err), is_resend: isResend, context: "auth_modal" });
        throw err;
      } finally {
        setTurnstileReset((n) => n + 1);
      }
    },
    [phone, turnstileToken]
  );

  const finish = useCallback(
    (data, newUser, method = "phone_otp") => {
      localStorage.setItem("accesstoken", data.access_token);
      // Identity is the backend user id from the response, never the phone.
      identify(data.user_id);
      signedInRef.current = true;
      track(EVENTS.SIGNED_IN, { method, is_new_user: !!newUser, purpose, market: getMarket(), context: "auth_modal" });
      if (newUser) track(EVENTS.SIGNED_UP, { method, context: "auth_modal" });
      onSuccess();
    },
    [onSuccess, purpose]
  );

  // The sheet closed by the person, not by signing in.
  const close = useCallback(() => {
    if (!signedInRef.current) {
      const where = !allMethods && !isIndia() ? "providers" : step;
      track(EVENTS.SIGN_IN_DISMISSED, { purpose, market: getMarket(), step: where, had_error: !!error });
    }
    onClose();
  }, [onClose, allMethods, step, purpose, error]);

  // Outside India: Google's ID token instead of a phone number.
  const signInWithGoogle = useCallback(
    async (idToken) => {
      if (!idToken || busy || providerBusy) return;
      setProviderBusy(true);
      setError("");
      try {
        const { data } = ok(await Api.GoogleLogin(idToken));
        finish(data, data.new_account === true, "google");
      } catch (err) {
        track(EVENTS.ERROR_SHOWN, { context: "google_login", reason: failReason(err) });
        setError("We couldn't sign you in with Google. Try again.");
      } finally {
        setProviderBusy(false);
      }
    },
    [busy, providerBusy, finish]
  );

  // Apple's identity token, as the iPhone app sends it. Apple gives the name
  // only on the very first authorisation, so it travels with the token.
  const signInWithApple = useCallback(
    async (identityToken, appleName) => {
      if (!identityToken || busy || providerBusy) return;
      setProviderBusy(true);
      setError("");
      try {
        const { data } = ok(await Api.AppleLogin(identityToken, appleName));
        finish(data, data.new_account === true, "apple");
      } catch (err) {
        track(EVENTS.ERROR_SHOWN, { context: "apple_login", reason: failReason(err) });
        setError("We couldn't sign you in with Apple. Try again.");
      } finally {
        setProviderBusy(false);
      }
    },
    [busy, providerBusy, finish]
  );

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const id = setInterval(() => setSecondsLeft((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [secondsLeft]);

  const sendOtp = useCallback(async () => {
    if (!phoneValid || busy) return;
    setBusy(true);
    setError("");
    try {
      await requestOtp(false);
      localStorage.setItem("phoneNo", phone);
      setStep("otp");
      setSecondsLeft(RESEND_SECONDS);
    } catch (err) {
      setError(
        err?.response?.status === 423
          ? "This number can't sign in right now. Contact support."
          : err?.response?.data?.error || "Couldn't send the code. Try again."
      );
    } finally {
      setBusy(false);
    }
  }, [phone, phoneValid, busy, requestOtp]);

  const verify = useCallback(async () => {
    if (otp.length < OTP_LENGTH || busy) return;
    setBusy(true);
    setError("");
    try {
      const { data } = ok(await Api.VerifyOtpV2({ phone, otp: Number(otp) }));
      if (data?.access_token) {
        track(EVENTS.OTP_VERIFIED, { is_new_user: false, method: "phone_otp", context: "auth_modal" });
        finish(data, false);
      } else if (data?.is_new_user && data?.otp_verified) {
        track(EVENTS.OTP_VERIFIED, { is_new_user: true, method: "phone_otp", context: "auth_modal" });
        // Verified, but there is no account yet: ask for a name to create one.
        setIsNewUser(true);
        setStep("name");
      } else {
        track(EVENTS.OTP_VERIFY_FAILED, { reason: "invalid_otp", context: "auth_modal" });
        setError("That code didn't work. Check and try again.");
        setOtp("");
      }
    } catch (err) {
      track(EVENTS.OTP_VERIFY_FAILED, { reason: failReason(err), context: "auth_modal" });
      setError(err?.response?.data?.error || "That code didn't work. Check and try again.");
      setOtp("");
    } finally {
      setBusy(false);
    }
  }, [phone, otp, busy, finish]);

  const nameValue = name.trim();
  const nameValid = nameValue.length > 0 && nameValue.length <= NAME_MAX && NAME_PATTERN.test(nameValue);

  const createAccount = useCallback(async () => {
    if (!nameValid || busy) return;
    setBusy(true);
    setError("");
    try {
      const { data } = ok(await Api.SignUpV2({ name: nameValue, phone }));
      if (data?.access_token) {
        finish(data, true);
      } else {
        setError("Couldn't create your account. Try again.");
      }
    } catch (err) {
      if (err?.response?.status === 403) {
        // The verification is good for 15 minutes and used once.
        setStep("phone");
        setOtp("");
        setError("That code has expired. Send a new one to continue.");
      } else {
        setError(err?.response?.data?.error || "Couldn't create your account. Try again.");
      }
    } finally {
      setBusy(false);
    }
  }, [nameValid, nameValue, phone, busy, finish]);

  useEffect(() => {
    if (step === "otp" && otp.length === OTP_LENGTH && !busy) verify();
  }, [otp, step, busy, verify]);

  const resend = async () => {
    try {
      await requestOtp(true);
      setSecondsLeft(RESEND_SECONDS);
    } catch {
      setError("Couldn't resend the code. Try again.");
    }
  };

  const boxes = Array.from({ length: OTP_LENGTH }, (_, i) => otp[i] || "");

  const india = isIndia();
  const hasProviders = !!(GOOGLE_CLIENT_ID || APPLE_SERVICES_ID);
  // Outside India the providers lead and the Indian number follows; in India
  // the number leads and the providers follow. Only with allMethods.
  const providersFirst = allMethods && !india && hasProviders;
  const providersAfter = allMethods && india && hasProviders;

  const providers = open && (
    <>
      {GOOGLE_CLIENT_ID && (
        <GoogleSignIn
          className={styles.google}
          onCredential={signInWithGoogle}
          onStart={() => track(EVENTS.SIGN_IN_STARTED, { method: "google", purpose, market: getMarket() })}
          onError={() => setError("Google sign-in didn't load. Check your connection and try again.")}
        />
      )}
      {APPLE_SERVICES_ID && (
        <AppleSignIn
          className={styles.apple}
          onCredential={signInWithApple}
          onError={() => setError("Apple sign-in didn't load. Check your connection and try again.")}
        />
      )}
      {providerBusy && <p className={styles.subtitle}>Signing you in…</p>}
    </>
  );

  if (!india && !allMethods) {
    const store = marketInfo(getMarket()).label;
    // Name the sign-ins this build offers: "Google or Apple", or just one.
    const providerNames = [GOOGLE_CLIENT_ID && "Google", APPLE_SERVICES_ID && "Apple"].filter(Boolean).join(" or ") || "Google";
    return (
      <Sheet open={open} onClose={close} label="Sign in">
        <p className={styles.title}>Sign in to Fleek</p>
        <p className={styles.subtitle}>
          {reason ||
            `Use your ${providerNames} account to buy in the ${store} store. Your plan and price are saved — you won't lose this page.`}
        </p>
        {hasProviders ? (
          providers
        ) : (
          <p className={styles.error} role="alert">Sign-in isn't available in this store yet.</p>
        )}
        {error && <p className={styles.error} role="alert">{error}</p>}
        <p className={styles.legal}>By continuing you agree to Fleek's Terms and Privacy Policy.</p>
      </Sheet>
    );
  }

  return (
    <Sheet open={open} onClose={close} label="Sign in">
      {step === "name" ? (
        <>
          <p className={styles.title}>What should we call you?</p>
          <p className={styles.subtitle}>+91 {phone} is verified. Add your name to create your account.</p>

          {isNewUser && <p className={styles.welcome}>First time here — welcome to Fleek.</p>}

          <div className={styles.phoneField}>
            <input
              ref={nameRef}
              className={styles.phoneInput}
              type="text"
              autoComplete="name"
              maxLength={NAME_MAX}
              placeholder="Your name"
              aria-label="Your name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setError("");
              }}
              onKeyDown={(e) => e.key === "Enter" && createAccount()}
            />
          </div>

          {name && !nameValid && !error && (
            <p className={styles.error} role="alert">Use letters and spaces only.</p>
          )}
          {error && <p className={styles.error} role="alert">{error}</p>}

          <button type="button" className={styles.primary} onClick={createAccount} disabled={!nameValid || busy}>
            {busy ? "Creating…" : "Create account"}
          </button>
        </>
      ) : step === "phone" ? (
        <>
          {providersFirst ? (
            <>
              <p className={styles.title}>Sign in to Fleek</p>
              <p className={styles.subtitle}>{reason || "Use the same sign-in as the Fleek app."}</p>
              {providers}
              <p className={styles.or}>or use an Indian mobile number</p>
            </>
          ) : (
            <>
              <p className={styles.title}>Enter your number</p>
              <p className={styles.subtitle}>
                {(allMethods && reason) ||
                  "We'll text a code. Your plan and price are saved — you won't lose this page."}
              </p>
            </>
          )}

          <div className={styles.phoneField}>
            <span className={styles.code}>+91</span>
            <span className={styles.divider} aria-hidden="true" />
            <input
              ref={phoneRef}
              className={styles.phoneInput}
              type="tel"
              inputMode="numeric"
              autoComplete="tel"
              maxLength={10}
              placeholder="98765 43210"
              aria-label="Mobile number"
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value.replace(/\D/g, "").slice(0, 10));
                setError("");
              }}
              onKeyDown={(e) => e.key === "Enter" && sendOtp()}
            />
          </div>

          {error && <p className={styles.error} role="alert">{error}</p>}

          <button
            type="button"
            className={styles.primary}
            onClick={sendOtp}
            disabled={!phoneValid || busy || providerBusy}
          >
            {busy ? "Sending…" : "Send code"}
          </button>

          {providersAfter && (
            <>
              <p className={styles.or}>or</p>
              {providers}
            </>
          )}

          <p className={styles.legal}>By continuing you agree to Fleek's Terms and Privacy Policy.</p>
        </>
      ) : (
        <>
          <p className={styles.title}>Enter the code</p>
          <p className={styles.subtitle}>
            Sent to +91 {phone} ·{" "}
            <button type="button" className={styles.link} onClick={() => setStep("phone")}>
              change
            </button>
          </p>

          {/* One real input behind the boxes keeps SMS autofill and paste working. */}
          <div className={styles.otpWrap} onClick={() => otpRef.current?.focus()}>
            <input
              ref={otpRef}
              className={styles.otpInput}
              type="tel"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={OTP_LENGTH}
              aria-label={`${OTP_LENGTH} digit code`}
              value={otp}
              onChange={(e) => {
                setOtp(e.target.value.replace(/\D/g, "").slice(0, OTP_LENGTH));
                setError("");
              }}
            />
            <div className={styles.otpBoxes} aria-hidden="true">
              {boxes.map((char, i) => (
                <span key={i} className={`${styles.otpBox} ${i === otp.length ? styles.otpBoxActive : ""}`}>
                  {char}
                </span>
              ))}
            </div>
          </div>

          {error && <p className={styles.error} role="alert">{error}</p>}

          <button
            type="button"
            className={`${styles.primary} ${styles.primaryOtp}`}
            onClick={verify}
            disabled={otp.length < OTP_LENGTH || busy}
          >
            {busy ? "Verifying…" : "Verify & continue"}
          </button>

          <p className={styles.resend}>
            {secondsLeft > 0 ? (
              `Didn't get it? Resend in 0:${String(secondsLeft).padStart(2, "0")}`
            ) : (
              <button type="button" className={styles.link} onClick={resend}>
                Resend code
              </button>
            )}
          </p>
        </>
      )}

      {/* Mounted only while the sheet is open, so pages don't load Cloudflare's script. */}
      {open && step !== "name" && (
        <Turnstile
          onToken={setTurnstileToken}
          resetKey={turnstileReset}
          action="web_auth_modal"
          className={styles.turnstile}
        />
      )}
    </Sheet>
  );
};

export default AuthModal;
